import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BrandMark, IconHome } from "@bht/ui";
import { ProfileAvatarButton, ProfileDrawer } from "./ProfileDrawer.js";

/**
 * Top-of-screen brand header for signed-in pages. Brand mark + product
 * name on the left; optional caller-supplied content + theme toggle +
 * profile avatar bubble on the right.
 *
 * Theme toggle lives in the header (not the profile drawer) so it's
 * one-tap discoverable. Cohort feedback: the drawer-buried toggle was
 * easy to miss.
 */
export function AppHeader({
  right,
}: {
  right?: React.ReactNode;
}) {
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Don't show the Home button when we're already on Home — it'd be a no-op
  // tap target and clutter the header.
  const onHome = location.pathname === "/app/home";

  function goHome() {
    // Drill and practice-test routes have unsaved-attempt risk on exit.
    const isMidFlow =
      location.pathname.startsWith("/app/drill/") ||
      location.pathname.startsWith("/app/practice-test/run/");
    if (isMidFlow) {
      const ok = window.confirm(
        "Leave this round? Your current question won't be saved.",
      );
      if (!ok) return;
    }
    navigate("/app/home");
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          /* 14px -> 10px vertical. A design review on a phone pointed out this
             strip costs about another navigation bar's worth of height, on a
             screen that already carries the browser's URL bar above it and our
             own tab bar below. Nothing here is content: it is a brand mark and
             three icons. The icons stay 44px of touch target via their own
             padding, so this takes the air, not the targets. */
          padding: "10px 20px",
          borderBottom: "1px solid var(--border-line)",
          background: "var(--bg-1)",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Link
          to="/app/home"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: "var(--fg-0)",
            /* Never let a crowded right side compress the brand into the
               progress text. Routes that pass `right` (Hidden Test's "1 of 8")
               were collapsing the two into each other on a 390px screen. */
            flexShrink: 0,
            // With the wordmark hidden on narrow screens the mark alone would be
            // a 26px target, so keep a 44px hit box around it.
            minHeight: 44,
            minWidth: 44,
          }}
        >
          <BrandMark size={26} />
          <span
            className="bht-appheader__wordmark"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-0)",
              whiteSpace: "nowrap",
            }}
          >
            BREAK·THE·TEST
          </span>
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
            /* Route-supplied status ("1 of 8") is a single token, never wrapped. */
            whiteSpace: "nowrap",
          }}
        >
          {right}
          {/* NO HOME BUTTON. Home was reachable three ways from this strip:
              the brand mark beside it, this icon, and the Home tab pinned to
              the bottom of the screen. Three affordances for one destination,
              drawn as three different symbols: a crosshair, a house and a
              bullseye. The tab bar is the canonical route. */}
          {/* Feedback and the theme toggle moved into the More sheet. Neither
              is an action on the study task in front of the student, and both
              were charging 44px of permanent navigation chrome on a 390px
              screen that already carries browser UI above and a tab bar below.
              Theme especially: it follows the OS by default, so for most
              students it is a setup decision made once, if ever. */}
          <ProfileAvatarButton onClick={() => setProfileOpen(true)} />
        </div>
      </div>
      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}
